import { Clock, MessageCircle, LogOut, XCircle } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

export default function PendingApprovalPage() {
  const { user, logout, refresh } = useAuth();
  const nav = useNavigate();
  const [adminPhone, setAdminPhone] = useState(null);
  const [checking, setChecking] = useState(false);

  useEffect(() => {
    api.get("/settings").then(({ data }) => setAdminPhone(data.admin_whatsapp || null)).catch(() => {});
  }, []);

  // Poll silently so the page flips to catalog the moment admin approves
  useEffect(() => {
    const id = setInterval(async () => {
      try { await refresh(); } catch {
        /* ignore */
      }
    }, 8000);
    return () => clearInterval(id);
  }, [refresh]);

  const checkNow = async () => {
    setChecking(true);
    try {
      await refresh();
      toast.success("Refreshed");
    } finally {
      setChecking(false);
    }
  };

  const followUpOnWhatsApp = () => {
    if (!adminPhone) return toast.error("Atelier WhatsApp not available");
    const phone = adminPhone.replace(/[^\d]/g, "");
    const lines = [
      "Namaste,",
      "",
      `I just registered on the Shubh Vivah portal and I'm waiting for approval.`,
      "",
      `Name: ${user?.name || ""}`,
      user?.business_name ? `Business: ${user.business_name}` : null,
      user?.phone ? `Phone: ${user.phone}` : null,
      `Email: ${user?.email || ""}`,
      "",
      "Could you please approve my access?",
    ].filter(Boolean);
    window.open(`https://wa.me/${phone}?text=${encodeURIComponent(lines.join("\n"))}`, "_blank", "noopener,noreferrer");
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] grid lg:grid-cols-2">
      <div
        className="hidden lg:block relative"
        style={{
          backgroundImage: "url('https://images.pexels.com/photos/32315685/pexels-photo-32315685.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940')",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="absolute inset-0 bg-[#D62828]/35" />
        <div className="absolute bottom-12 left-12 right-12 text-white">
          <div className="tracking-luxe text-[10px] text-[#F4A100] mb-3">By invitation</div>
          <p className="font-serif text-3xl leading-tight max-w-md">Our private atelier opens its doors only to trusted partners.</p>
        </div>
      </div>

      <div className="flex items-center justify-center p-8 md:p-16 bg-[#FAF9F6]">
        {user?.status === "rejected" ? (
          <div className="w-full max-w-md sv-anim-in" data-testid="rejected-notice">
            <div className="w-14 h-14 bg-[#FEF2F2] border border-[#991B1B] flex items-center justify-center mb-6">
              <XCircle size={22} className="text-[#991B1B]" strokeWidth={1.5} />
            </div>
            <div className="tracking-luxe text-[10px] text-[#991B1B] mb-3">Access not granted</div>
            <h1 className="font-serif text-4xl md:text-5xl text-[#D62828] mb-3">Your access was not approved</h1>
            <p className="text-[#4A4A4A] mb-8 leading-relaxed">
              Namaste <span className="text-[#1A1A1A]">{user?.name?.split(" ")[0] || ""}</span>, the atelier has not granted access to your account at this time. If you believe this is a mistake or you&apos;d like to discuss, please reach out on WhatsApp.
            </p>

            <div className="bg-white border border-[#E5E0D8] p-5 mb-6">
              <div className="tracking-luxe text-[10px] text-[#F4A100] mb-3">Your application</div>
              <dl className="space-y-2 text-sm">
                <Row k="Name" v={user?.name} />
                {user?.business_name && <Row k="Business" v={user.business_name} />}
                {user?.city && <Row k="City" v={user.city} />}
                <Row k="Email" v={user?.email} />
                {user?.phone && <Row k="Phone" v={user.phone} />}
              </dl>
            </div>

            <div className="flex flex-wrap gap-3">
              <button
                onClick={followUpOnWhatsApp}
                disabled={!adminPhone}
                data-testid="rejected-contact"
                className="inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#1ebe57] disabled:opacity-50 text-white px-5 py-3 transition-colors"
              >
                <MessageCircle size={16} /> <span className="tracking-luxe text-[10px]">Contact the Atelier</span>
              </button>
              <button
                onClick={() => { logout(); nav("/login"); }}
                data-testid="rejected-logout"
                className="inline-flex items-center gap-2 text-[#4A4A4A] hover:text-[#D62828] px-3 py-3 transition-colors"
              >
                <LogOut size={14} /> <span className="text-sm">Sign out</span>
              </button>
            </div>
          </div>
        ) : (
        <div className="w-full max-w-md sv-anim-in" data-testid="pending-approval">
          <div className="w-14 h-14 bg-[#F5F2EA] border border-[#F4A100] flex items-center justify-center mb-6">
            <Clock size={22} className="text-[#D62828]" strokeWidth={1.5} />
          </div>
          <div className="tracking-luxe text-[10px] text-[#F4A100] mb-3">Application received</div>
          <h1 className="font-serif text-4xl md:text-5xl text-[#D62828] mb-3">Awaiting atelier approval</h1>
          <p className="text-[#4A4A4A] mb-8 leading-relaxed">
            Namaste <span className="text-[#1A1A1A]">{user?.name?.split(" ")[0] || ""}</span>, thank you for registering. To keep our designs private to trusted partners, every new account is reviewed by our team. You&apos;ll get access the moment we approve.
          </p>

          <div className="bg-white border border-[#E5E0D8] p-5 mb-6">
            <div className="tracking-luxe text-[10px] text-[#F4A100] mb-3">Your application</div>
            <dl className="space-y-2 text-sm">
              <Row k="Name" v={user?.name} />
              {user?.business_name && <Row k="Business" v={user.business_name} />}
              {user?.city && <Row k="City" v={user.city} />}
              <Row k="Email" v={user?.email} />
              {user?.phone && <Row k="Phone" v={user.phone} />}
            </dl>
          </div>

          <div className="flex flex-wrap gap-3">
            <button
              onClick={followUpOnWhatsApp}
              disabled={!adminPhone}
              data-testid="follow-up-whatsapp"
              className="inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#1ebe57] disabled:opacity-50 text-white px-5 py-3 transition-colors"
            >
              <MessageCircle size={16} /> <span className="tracking-luxe text-[10px]">Follow up on WhatsApp</span>
            </button>
            <button
              onClick={checkNow}
              disabled={checking}
              data-testid="check-status"
              className="inline-flex items-center gap-2 border border-[#F4A100] text-[#D62828] hover:bg-[#F5F2EA] px-5 py-3 transition-colors"
            >
              <span className="tracking-luxe text-[10px]">{checking ? "Checking…" : "Check status"}</span>
            </button>
            <button
              onClick={() => { logout(); nav("/login"); }}
              data-testid="pending-logout"
              className="inline-flex items-center gap-2 text-[#4A4A4A] hover:text-[#D62828] px-3 py-3 transition-colors"
            >
              <LogOut size={14} /> <span className="text-sm">Sign out</span>
            </button>
          </div>

          <p className="mt-8 text-xs text-[#4A4A4A]">This page refreshes automatically. The moment your account is approved, your catalog will appear here.</p>
        </div>
        )}
      </div>
    </div>
  );
}

function Row({ k, v }) {
  return (
    <div className="flex justify-between gap-3">
      <dt className="text-[#4A4A4A]">{k}</dt>
      <dd className="text-[#1A1A1A] text-right truncate max-w-[60%]">{v}</dd>
    </div>
  );
}
